/**
 * Skills e MCPs, the page's own logic: turning what the person typed into what the desktop broker
 * takes (desktop/src-tauri/src/skills.rs and mcp.rs), and reading what the local AI answered to the
 * catalog prompts `skills.install-from-repo` and `mcp.install-from-repo`.
 */

/** A repository address to send to the local AI: https, no user name or token in it. */
export function checkRepoUrl(value: string): 'ok' | 'invalid' | 'credentials' {
  let url: URL
  try {
    url = new URL(value.trim())
  } catch {
    return 'invalid'
  }
  if (url.protocol !== 'https:' || !url.hostname || value.trim().length > 1000) return 'invalid'
  if (url.username || url.password) return 'credentials'
  return 'ok'
}

/** A catalog prompt with the repository's address in place of `{{url}}`. */
export function fillUrl(template: string, url: string): string {
  return template.split('{{url}}').join(url.trim())
}

const PACKAGE = /^(@[a-z0-9][a-z0-9._-]*\/)?[a-z0-9][a-z0-9._-]*(@[A-Za-z0-9._-]+)?$/

/**
 * "Pelo pacote": an npm package (`@scope/name`, `name@1.2.0`), or the `npx -y …` line a README
 * shows, as the JSON the broker takes. Null when it is not a package name.
 */
export function packageToConfig(input: string): { name: string; json: string } | null {
  const words = input.trim().split(/\s+/).filter(Boolean)
  if (words[0] === 'npx') words.shift()
  while (words[0] === '-y' || words[0] === '--yes') words.shift()
  const pkg = words.shift()
  if (!pkg || !PACKAGE.test(pkg) || pkg.length > 214) return null
  if (words.some((w) => /[\n\r&|<>^%"]/.test(w))) return null
  const base = pkg.replace(/@[^@/]*$/, '').split('/').pop() ?? pkg
  const name = base.replace(/^mcp-server-|^server-|^mcp-/, '').replace(/-mcp(-server)?$/, '').replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'mcp'
  return { name, json: JSON.stringify({ mcpServers: { [name]: { command: 'npx', args: ['-y', pkg, ...words] } } }, null, 2) }
}

/** The configuration the local AI wrote between `<vephus-mcp>` markers, or null. */
export function readMcpReply(reply: string | null | undefined): string | null {
  const match = /<vephus-mcp>\s*([\s\S]*?)\s*<\/vephus-mcp>/.exec(reply ?? '')
  if (!match?.[1]) return null
  const body = match[1].replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '')
  try {
    JSON.parse(body)
  } catch {
    return null
  }
  return body
}

/** The skills the local AI said it installed (`VEPHUS-SKILLS-INSTALLED: a, b`). */
export function readInstalledSkills(reply: string | null | undefined): string[] {
  const match = /^VEPHUS-SKILLS-INSTALLED:(.*)$/m.exec(reply ?? '')
  return (match?.[1] ?? '').split(',').map((s) => s.trim()).filter((s) => s && s !== '<name>')
}

const MCP_ERRORS = ['invalid-json', 'no-server', 'name-required', 'invalid-name', 'invalid-url', 'invalid-command', 'invalid-args', 'invalid-env', 'invalid-headers',
  'invalid-type', 'too-many', 'not-found', 'command-not-found', 'timeout', 'exited', 'unsafe-argument', 'sse-not-tested', 'invalid-package']

/** A broker error ("invalid-name:x", "http-401", "server: …") in the person's words. */
export function mcpProblem(t: (key: string, vars?: Record<string, string | number>) => string, error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error)
  const coded = raw.includes(':') && !/^(server|network|spawn):/.test(raw)
  const [code, detail] = coded ? [raw.slice(0, raw.indexOf(':')), raw.slice(raw.indexOf(':') + 1)] : [raw, '']
  if (MCP_ERRORS.includes(code)) return t(`mcp.error.${code}`, { detail })
  if (/^http-\d+$/.test(raw)) return t('mcp.error.http', { status: raw.slice(5) })
  return t('mcp.error.unknown', { detail: raw })
}

/** The `${VARIABLE}` references still in a configuration: values the person has to fill in. */
export function unfilledReferences(json: string): string[] {
  return [...new Set([...json.matchAll(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g)].map((m) => m[1]!))]
}
